/**
 * ResetPasswordPage — schließt den Passwort-Reset ab (Link aus der E-Mail).
 *
 * Liest ?token aus der URL, lässt den Nutzer ein neues Passwort (2× zur Bestätigung)
 * setzen und ruft POST /auth/reset-password. Bei Erfolg → weiter zur Anmeldung.
 * Öffentliche Seite (kein Login nötig).
 */
import { useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Lock, ShieldCheck, ArrowRight } from "lucide-react";
import { resetPassword } from "@/lib/authToken";

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!token) { setError("Der Link ist unvollständig. Bitte fordere einen neuen an."); return; }
    if (password.length < 8) { setError("Das Passwort muss mindestens 8 Zeichen lang sein."); return; }
    if (password !== confirm) { setError("Die Passwörter stimmen nicht überein."); return; }
    setLoading(true);
    try {
      await resetPassword(token, password);
      setDone(true);
      toast.success("Passwort geändert — du kannst dich jetzt anmelden.");
      setTimeout(() => navigate("/login?tab=login", { replace: true }), 1800);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string"
        ? detail
        : "Der Link ist ungültig oder abgelaufen. Bitte fordere einen neuen an.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-stone-50 flex flex-col items-center justify-center px-4 py-12">
      <div className="flex items-center gap-3 mb-8">
        <img src="/images/logo.png" alt="Förderfuchs" width="240" height="276" className="h-12 w-auto" />
        <div className="leading-tight">
          <div className="font-heading font-extrabold text-xl text-stone-900">KI Social Media Manager</div>
          <div className="text-xs text-stone-500 tracking-wide">by Förderfuchs</div>
        </div>
      </div>

      <div className="w-full max-w-md bg-white rounded-2xl border border-stone-200 shadow-sm p-7">
        <h1 className="font-heading font-bold text-lg text-stone-800 mb-1">Neues Passwort festlegen</h1>
        <p className="text-sm text-stone-500 mb-5">
          Wähle ein neues Passwort für dein Förderfuchs-Konto.
        </p>

        {done ? (
          <div className="text-sm text-green-700 bg-green-50 border border-green-100 rounded-lg px-3 py-3" data-testid="reset-done">
            Dein Passwort wurde geändert. Wir leiten dich zur Anmeldung weiter…
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <label className="block">
              <span className="text-xs font-medium text-stone-500">Neues Passwort</span>
              <div className="mt-1 flex items-center gap-2 rounded-lg border border-stone-200 px-3 focus-within:ring-2 focus-within:ring-orange-200">
                <Lock size={15} className="text-stone-400" />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Mind. 8 Zeichen"
                  autoComplete="new-password"
                  data-testid="reset-password"
                  className="flex-1 py-2.5 text-sm outline-none bg-transparent"
                />
              </div>
            </label>
            <label className="block">
              <span className="text-xs font-medium text-stone-500">Passwort bestätigen</span>
              <div className="mt-1 flex items-center gap-2 rounded-lg border border-stone-200 px-3 focus-within:ring-2 focus-within:ring-orange-200">
                <Lock size={15} className="text-stone-400" />
                <input
                  type="password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  placeholder="Passwort wiederholen"
                  autoComplete="new-password"
                  data-testid="reset-confirm"
                  className="flex-1 py-2.5 text-sm outline-none bg-transparent"
                />
              </div>
            </label>

            {error && (
              <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2" data-testid="reset-error">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              data-testid="reset-submit"
              className="w-full flex items-center justify-center gap-2 bg-fox-fire hover:bg-orange-600 text-white font-medium py-2.5 rounded-lg transition-all disabled:opacity-60"
            >
              {loading ? <Loader2 size={18} className="animate-spin" /> : <ArrowRight size={18} />}
              Passwort speichern
            </button>
          </form>
        )}

        <p className="text-center text-sm text-stone-500 mt-5">
          <Link to="/login?tab=login" className="text-fox-fire font-medium hover:underline">
            Zurück zur Anmeldung
          </Link>
        </p>
      </div>

      <div className="mt-6 flex items-center justify-center gap-1.5 text-xs text-stone-500">
        <ShieldCheck size={14} className="text-fox-fire" /> Sichere, verschlüsselte Übertragung
      </div>
    </div>
  );
}
